import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import './home.css';
import { useGet } from '../../Api';
import { base_url } from '../../Api/apiConfig';
import FavoriteButton from '../../Components/FavoriteButton';
import { VacationsHeader, SearchPill, VacationsFooter } from '../../Components/VacationsChrome';
import {
    heroBg,
    stayCapCana, stayMontego, stayRodney,
    destCapCana, destMontego, destRodney, destCancun, destBarbados,
    avatarAlex, avatarSam, avatarPriya,
} from '../../Assets/vacations';

const MAX_AMENITY_CHIPS = 10;

const destinations = [
    { name: 'Cap Cana & Bávaro', query: 'Cap Cana', image: destCapCana },
    { name: 'Montego Bay & Tryall', query: 'Montego Bay', image: destMontego },
    { name: 'Rodney Bay & Gros Islet', query: 'Rodney Bay', image: destRodney },
    { name: 'Cancún & Playa del Carmen', query: 'Cancún', image: destCancun },
    { name: 'Barbados', query: 'Barbados', image: destBarbados },
];

const packages = [
    { title: 'Cap Cana Villa + Reef Day', price: 'From USD $1,890 / 4 nights', blurb: 'Four-night pool villa stay with a guided reef snorkel included.', meta: ['Villa', '+ adventure', 'sleeps 8'], loc: 'Cap Cana, Dominican Republic', image: stayCapCana },
    { title: 'Montego Bay Condo + Sunset Sail', price: 'From USD $980 / 3 nights', blurb: 'Ocean-view condo with a golden-hour catamaran add-on for two.', meta: ['Condo', '+ sail', 'sleeps 4'], loc: 'Montego Bay, Jamaica', image: stayMontego },
    { title: 'Rodney Bay Suite + Rainforest Hike', price: 'From USD $740 / 3 nights', blurb: 'Garden suite stay plus a full-day guided peak trail with waterfall stops.', meta: ['Boutique', '+ hike', 'sleeps 2'], loc: 'Rodney Bay, St. Lucia', image: stayRodney },
];

const reviews = [
    { name: 'Alex C.', place: 'Dominican Republic', avatar: avatarAlex, quote: 'Our Cap Cana villa rental was spotless and easy to book — the perfect try-before-you-buy week.' },
    { name: 'Sam R.', place: 'Jamaica', avatar: avatarSam, quote: 'Montego Bay condo felt like home; we’ll be back when we’re ready to buy.' },
    { name: 'Priya N.', place: 'St. Lucia', avatar: avatarPriya, quote: 'Rodney Bay suite was quiet, charming, and perfect for evening marina walks.' },
];

const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    if (String(imageUrl).startsWith('http')) return imageUrl;
    return `${base_url}/storage/${String(imageUrl).replace(/^\/+/, '')}`;
};

export const StayCard = ({ item }) => {
    const image = getImageUrl((item?.media?.length > 0 ? item.media : item?.linkedMedia || [])[0]?.image_url);
    const beds = item?.bedrooms?.length;
    const sleeps = (item?.bedrooms || []).reduce((sum, b) => sum + (Number(b?.no_of_guests) || 0), 0);
    const location = [item?.location?.city, item?.location?.country].filter(Boolean).join(', ');
    const plainDescription = item?.description ? String(item.description).replace(/[#*_`>]/g, '').replace(/\s+/g, ' ').trim() : '';
    const blurb = plainDescription.slice(0, 110);

    return (
        <article className="listing-card">
            <div className="listing-thumb">
                <Link to={`/listing-detial/${item?.id}`} aria-label={item?.title}>
                    {image && <img src={image} alt={item?.title || ''} loading="lazy" />}
                </Link>
                <div className="fav-wrap"><FavoriteButton listingId={item?.id} /></div>
            </div>
            <div className="listing-body">
                <div className="listing-price">USD ${item?.pricing?.nightly} / night</div>
                <h3><Link to={`/listing-detial/${item?.id}`}>{item?.title}</Link></h3>
                {blurb && <p className="listing-blurb">{blurb}{plainDescription.length > 110 ? '…' : ''}</p>}
                <div className="listing-meta">
                    {beds ? <span>🛏 {beds}</span> : null}
                    {item?.bathrooms ? <span>🛁 {item.bathrooms}</span> : null}
                    {sleeps ? <span>sleeps {sleeps}</span> : null}
                </div>
                {location && <div className="listing-loc">📍 {location}</div>}
            </div>
        </article>
    );
};

export const VacationsHome = () => {
    const [listings, setListings] = useState([]);
    const [amenities, setAmenities] = useState([]);
    const [selectedAmenityId, setSelectedAmenityId] = useState(null);
    const [isFetching, setIsFetching] = useState(true);
    // Featured Stays section: on/off and how many stays, both set by the admin (Listing Rules page)
    const [settings, setSettings] = useState({ home_featured_enabled: true, home_featured_count: 6 });
    const [settingsLoaded, setSettingsLoaded] = useState(false);
    const { ApiData: AmenitiesData, get: GetAmenities } = useGet('/amenities');

    const selectedAmenityRef = useRef(null);
    const countRef = useRef(6);

    const fetchListings = useCallback(async (amenityId = selectedAmenityRef.current) => {
        setIsFetching(true);
        try {
            const params = new URLSearchParams();
            params.set('per_page', String(countRef.current));
            if (amenityId != null) params.set('amenities', String(amenityId));

            const token = localStorage.getItem('login');
            const response = await fetch(`${base_url}/frontend/listings?${params.toString()}`, {
                method: 'GET',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });
            if (!response.ok) throw new Error('Network response was not ok');
            const result = await response.json();
            setListings(result?.data?.listings?.data || []);
        } catch (err) {
            console.error(err);
            setListings([]);
        } finally {
            setIsFetching(false);
        }
    }, []);

    useEffect(() => {
        document.title = 'Kayifi Vacations';
        let alive = true;
        fetch(`${base_url}/site-settings`, { headers: { Accept: 'application/json' } })
            .then((r) => r.json())
            .then((res) => (res?.data ? res.data : null))
            .catch(() => null)
            .then((data) => {
                if (!alive) return;
                const next = {
                    home_featured_enabled: data ? data.home_featured_enabled !== false : true,
                    home_featured_count: Math.max(1, Number(data?.home_featured_count) || 6),
                };
                countRef.current = next.home_featured_count;
                setSettings(next);
                setSettingsLoaded(true);
                if (next.home_featured_enabled) {
                    fetchListings(null);
                    GetAmenities();
                } else {
                    setIsFetching(false);
                }
            });
        return () => { alive = false; };
    }, []);

    useEffect(() => {
        if (AmenitiesData) setAmenities(AmenitiesData?.data?.data || []);
    }, [AmenitiesData]);

    const handleAmenityClick = (amenityId) => {
        const nextId = selectedAmenityRef.current === amenityId ? null : amenityId;
        selectedAmenityRef.current = nextId;
        setSelectedAmenityId(nextId);
        setListings([]);
        fetchListings(nextId);
    };

    return (
        <div className="vx-home">
            <VacationsHeader variant="home" />
            <SearchPill />

            <section className="vac-hero" style={{ backgroundImage: `url(${heroBg})` }}>
                <div className="container">
                    <p className="eyebrow" style={{ color: 'var(--primary-mid)' }}>vacations.kayifi.com</p>
                    <h1>Stay longer. Fall in love with the view.</h1>
                    <p className="sub">Hand-picked villas, condos, and boutique stays across the Caribbean &amp; Mexico.</p>
                </div>
            </section>

            {settings.home_featured_enabled && (
            <section className="section" style={{ paddingTop: '2rem', paddingBottom: '1rem' }}>
                <div className="container">
                    <div className="chips-row" role="list">
                        <button className={`chip${selectedAmenityId === null ? ' active' : ''}`} type="button" onClick={() => selectedAmenityId !== null && handleAmenityClick(selectedAmenityId)}>All</button>
                        {amenities.slice(0, MAX_AMENITY_CHIPS).map((item) => (
                            <button key={item.id} className={`chip${selectedAmenityId === item.id ? ' active' : ''}`} type="button" onClick={() => handleAmenityClick(item.id)}>
                                {item.amenity_name}
                            </button>
                        ))}
                    </div>
                </div>
            </section>
            )}

            {settings.home_featured_enabled && (
            <section className="section" id="featured" style={{ paddingTop: '2rem' }}>
                <div className="container">
                    <div className="section-head">
                        <div>
                            <span className="eyebrow">Featured stays</span>
                            <h2>Stay longer. Fall in love.</h2>
                            <p>Hand-picked vacation rentals from our hosts.</p>
                        </div>
                        <Link className="btn btn-outline-dark btn-sm" to="/listings">View All Stays</Link>
                    </div>
                    <div className="listings-grid">
                        {listings.map((item) => <StayCard key={item.id} item={item} />)}
                        {!isFetching && settingsLoaded && !listings.length && <p className="empty-note">No listings found for this category.</p>}
                    </div>
                </div>
            </section>
            )}

            {listings.length >= 3 && (
                <section className="section section-alt" id="guest-favorites">
                    <div className="container">
                        <div className="section-head">
                            <div>
                                <span className="eyebrow">Guest favorites</span>
                                <h2>Stays travelers love</h2>
                            </div>
                        </div>
                        <div className="favorites-grid">
                            {listings.slice(0, 3).map((item) => {
                                const image = getImageUrl((item?.media?.length > 0 ? item.media : item?.linkedMedia || [])[0]?.image_url);
                                return (
                                    <Link className="fav-card" to={`/listing-detial/${item.id}`} key={item.id}>
                                        {image && <img src={image} alt="" />}
                                        <div className="fav-card-body">
                                            <h3>{item.title}</h3>
                                            <p>{[item?.location?.city, item?.location?.country].filter(Boolean).join(', ')}</p>
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            <section className="section" id="destinations">
                <div className="container">
                    <div className="section-head">
                        <div>
                            <span className="eyebrow">Browse by destination</span>
                            <h2>Island stays await</h2>
                        </div>
                    </div>
                    <div className="country-grid">
                        {destinations.map((d) => (
                            <Link className="country-card" to={`/search?location=${encodeURIComponent(d.query)}`} key={d.name}>
                                <img src={d.image} alt="" /><span>{d.name}</span>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <section className="section section-alt" id="packages">
                <div className="container">
                    <div className="section-head">
                        <div>
                            <span className="eyebrow">Packages</span>
                            <h2>Stay + experience bundles</h2>
                            <p>Pair a stay with island days.</p>
                        </div>
                    </div>
                    <div className="listings-grid">
                        {packages.map((p) => (
                            <article className="listing-card" key={p.title}>
                                <div className="listing-thumb">
                                    <img src={p.image} alt={p.title} loading="lazy" />
                                    <div className="badges"><span className="badge">Package</span></div>
                                </div>
                                <div className="listing-body">
                                    <div className="listing-price">{p.price}</div>
                                    <h3>{p.title}</h3>
                                    <p className="listing-blurb">{p.blurb}</p>
                                    <div className="listing-meta">{p.meta.map((m) => <span key={m}>{m}</span>)}</div>
                                    <div className="listing-loc">📍 {p.loc}</div>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section className="section section-alt" id="list-cta">
                <div className="container">
                    <div className="cta-list">
                        <div>
                            <h2>Find a stay that feels like yours</h2>
                            <p>Browse vacation rentals — or list your own property as a host.</p>
                        </div>
                        <div className="cta-actions">
                            <Link className="btn btn-primary" to="/listings">Find A Stay</Link>
                            <Link className="btn btn-outline-light" to="/sign-up">List Your Rental</Link>
                        </div>
                    </div>
                </div>
            </section>

            <section className="section" id="reviews">
                <div className="container">
                    <div className="section-head">
                        <div>
                            <span className="eyebrow">Customer Reviews</span>
                            <h2>What vacation guests say</h2>
                        </div>
                    </div>
                    <div className="reviews-grid">
                        {reviews.map((r) => (
                            <article className="review-card" key={r.name}>
                                <span className="review-badge">Vacations</span>
                                <div className="review-stars" aria-label="5 out of 5 stars">★★★★★</div>
                                <p className="review-quote">“{r.quote}”</p>
                                <div className="review-author">
                                    <img className="review-avatar" src={r.avatar} alt={r.name} width="96" height="96" />
                                    <div>
                                        <strong>{r.name}</strong>
                                        <span>Vacation guest · {r.place} · Vacations</span>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <VacationsFooter variant="home" />
        </div>
    );
};
